import { useEffect, useState } from 'react';
import { Delete, Mic, MicOff, Phone, PhoneOff, X } from 'lucide-react';
import { useSoftphone, type SoftphoneStatus } from '@/lib/softphone';
import { formatPhone, formatDuration } from '@/lib/format';
import { cn } from '@/lib/cn';

/** Turn typed digits into an E.164 number for a US-first dialer. */
function toE164(input: string): string {
  const trimmed = input.trim();
  if (trimmed.startsWith('+')) return '+' + trimmed.replace(/[^\d]/g, '');
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return digits ? `+${digits}` : '';
}

const STATUS_DOT: Record<SoftphoneStatus, string> = {
  offline: 'bg-[var(--color-foreground-subtle)]',
  ready: 'bg-[var(--color-success)]',
  connecting: 'bg-[var(--color-warning)]',
  incoming: 'bg-[var(--color-primary)]',
  oncall: 'bg-[var(--color-success)]',
};

export function SoftphoneWidget() {
  const sp = useSoftphone();

  if (sp.status === 'incoming') {
    return (
      <Shell>
        <IncomingCard from={sp.incomingFrom} onAccept={sp.acceptCall} onReject={sp.rejectCall} />
      </Shell>
    );
  }

  if (sp.status === 'connecting' || sp.status === 'oncall') {
    return (
      <Shell>
        <ActiveCard
          number={sp.activeNumber}
          connecting={sp.status === 'connecting'}
          muted={sp.muted}
          onMute={sp.toggleMute}
          onHangUp={sp.hangUp}
        />
      </Shell>
    );
  }

  return <IdleWidget status={sp.status} onCall={sp.makeCall} />;
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="fixed bottom-4 right-4 z-50 w-72">{children}</div>;
}

function IdleWidget({
  status,
  onCall,
}: {
  status: SoftphoneStatus;
  onCall: (to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [digits, setDigits] = useState('');

  function press(key: string) {
    setDigits((d) => d + key);
  }

  function call() {
    const to = toE164(digits);
    if (to) {
      onCall(to);
      setDigits('');
      setOpen(false);
    }
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {open ? (
        <div className="mb-2 w-64 rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-3 shadow-lg">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-[var(--color-foreground-muted)]">New call</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Close dialer"
            >
              <X className="size-4" />
            </button>
          </div>
          <div className="mb-2 flex items-center gap-2">
            <input
              value={digits}
              onChange={(e) => setDigits(e.target.value)}
              placeholder="(509) 555-1234"
              inputMode="tel"
              className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--color-primary)]/50"
            />
            <button
              type="button"
              onClick={() => setDigits((d) => d.slice(0, -1))}
              className="text-muted-foreground hover:text-foreground"
              aria-label="Delete"
            >
              <Delete className="size-4" />
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'].map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => press(k)}
                className="rounded-md py-2 text-sm font-medium hover:bg-muted/50"
              >
                {k}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={call}
            disabled={status !== 'ready' || !digits}
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-md bg-[var(--color-success)] py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Phone className="size-4" /> Call
          </button>
          {status !== 'ready' ? (
            <p className="mt-1.5 text-center text-[11px] text-muted-foreground">
              Softphone {status === 'offline' ? 'offline' : status}…
            </p>
          ) : null}
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative flex size-12 items-center justify-center rounded-full bg-[var(--color-primary)] text-white shadow-lg transition-transform hover:scale-105"
        title={`Softphone: ${status}`}
        aria-label="Softphone"
      >
        <Phone className="size-5" />
        <span
          className={cn(
            'absolute -right-0 -top-0 size-3 rounded-full ring-2 ring-[var(--color-surface)]',
            STATUS_DOT[status],
          )}
        />
      </button>
    </div>
  );
}

function IncomingCard({
  from,
  onAccept,
  onReject,
}: {
  from: string | null;
  onAccept: () => void;
  onReject: () => void;
}) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-xl">
      <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-primary)]">
        Incoming call
      </div>
      <div className="mt-1 text-lg font-semibold">{from ? formatPhone(from) : 'Unknown'}</div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onReject}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--color-danger)] py-2 text-sm font-medium text-white"
        >
          <PhoneOff className="size-4" /> Decline
        </button>
        <button
          type="button"
          onClick={onAccept}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--color-success)] py-2 text-sm font-medium text-white"
        >
          <Phone className="size-4" /> Accept
        </button>
      </div>
    </div>
  );
}

function ActiveCard({
  number,
  connecting,
  muted,
  onMute,
  onHangUp,
}: {
  number: string | null;
  connecting: boolean;
  muted: boolean;
  onMute: () => void;
  onHangUp: () => void;
}) {
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (connecting) return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [connecting]);

  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 shadow-xl">
      <div className="text-xs font-medium uppercase tracking-wide text-[var(--color-foreground-muted)]">
        {connecting ? 'Calling…' : 'On call'}
      </div>
      <div className="mt-1 flex items-baseline justify-between">
        <span className="text-lg font-semibold">{number ? formatPhone(number) : 'Unknown'}</span>
        {!connecting ? (
          <span className="text-sm tabular-nums text-muted-foreground">{formatDuration(seconds)}</span>
        ) : null}
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onMute}
          className={cn(
            'flex flex-1 items-center justify-center gap-2 rounded-md border py-2 text-sm font-medium',
            muted
              ? 'border-[var(--color-warning)]/40 bg-[var(--color-warning)]/10 text-[var(--color-warning)]'
              : 'border-[var(--color-border)] hover:bg-muted/50',
          )}
        >
          {muted ? <MicOff className="size-4" /> : <Mic className="size-4" />}
          {muted ? 'Muted' : 'Mute'}
        </button>
        <button
          type="button"
          onClick={onHangUp}
          className="flex flex-1 items-center justify-center gap-2 rounded-md bg-[var(--color-danger)] py-2 text-sm font-medium text-white"
        >
          <PhoneOff className="size-4" /> End
        </button>
      </div>
    </div>
  );
}
